/**
 * CompletionPolicyPanel — DEC-329: Settings → "Dispatcher completes a driver's ticket".
 *
 * GET / PATCH /v1/tenants/completion-policy. Anyone who sees the panel can read the setting;
 * only MANAGE_TENANT holders (TENANT_ADMIN, PLATFORM_ADMIN — `canEdit`) can change it, which is
 * the API's own gate. A manager sees it read-only with a line saying who can change it.
 *
 * Plain words for the three values (Corbin, 2026-10-13; new companies start on "needs a
 * manager's OK", existing ones were set to "allowed"):
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation, useApiOpts } from '@lodex-pro/web-shared';
import {
  COMPLETION_POLICY_QUERY_KEY,
  useCompletionPolicy,
  type DispatcherCompletionPolicy,
} from './completion-policy';

export const COMPLETION_POLICY_OPTIONS: ReadonlyArray<{
  readonly value: DispatcherCompletionPolicy;
  readonly label: string;
  readonly help: string;
}> = [
  {
    value: 'SUPERVISOR_REQUIRED',
    label: "Needs a manager's OK",
    help: 'A dispatcher can ask; a dispatch manager or admin approves before the ticket moves to review.',
  },
  {
    value: 'ALWAYS',
    label: 'Allowed',
    help: 'Dispatchers can send a stuck ticket with a captured load to review themselves, with a reason.',
  },
  {
    value: 'NEVER',
    label: 'Never',
    help: 'Only the driver can finish a ticket. Stuck tickets stay on the stuck list until they sync.',
  },
];

export interface CompletionPolicyPanelProps {
  readonly canEdit: boolean;
}

export function CompletionPolicyPanel({ canEdit }: CompletionPolicyPanelProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useCompletionPolicy(apiOpts);

  const mutation = useApiMutation<{ policy: DispatcherCompletionPolicy }, { policy: string }>(
    apiOpts,
    '/v1/tenants/completion-policy',
    'PATCH',
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: COMPLETION_POLICY_QUERY_KEY });
        toast.success('Saved');
      },
      onError: () => {
        toast.error('Could not save the setting. Try again.');
      },
    },
  );

  return (
    <div style={{ maxWidth: '640px' }} data-testid="completion-policy-panel">
      <h2
        style={{
          fontSize: '18px',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          margin: '0 0 4px',
        }}
      >
        Dispatcher completes a driver&apos;s ticket
      </h2>
      <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
        When a stuck ticket already has a load captured, can a dispatcher send it to review for the
        driver?
      </p>
      {isError ? (
        <p style={{ color: 'var(--color-danger)', fontSize: '14px' }}>
          Failed to load this setting.
        </p>
      ) : isLoading || data === undefined ? (
        <p style={{ color: 'var(--color-text-tertiary)', fontSize: '14px' }}>Loading…</p>
      ) : (
        <div
          role="radiogroup"
          aria-label="Dispatcher completion policy"
          style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
        >
          {COMPLETION_POLICY_OPTIONS.map((o) => (
            <label
              key={o.value}
              style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
                cursor: canEdit ? 'pointer' : 'default',
              }}
            >
              <input
                type="radio"
                name="dispatcher-completion-policy"
                value={o.value}
                checked={data.policy === o.value}
                disabled={!canEdit || mutation.isPending}
                onChange={(): void => mutation.mutate({ policy: o.value })}
              />
              <span>
                <span
                  style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)' }}
                >
                  {o.label}
                </span>
                <span
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {o.help}
                </span>
              </span>
            </label>
          ))}
          {!canEdit ? (
            <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
              Only a company admin can change this.
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
